import pdfImage from "../../image/pdf.jpg";

export const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
export const ALLOWED_FILE_TYPES = ["image/jpeg", "image/png"];
export const PROOF_ALLOWED_FILE_TYPES = [
  "image/jpeg",
  "image/png",
  "application/pdf",
];

export const readFile = (file) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(file);
  });
};

export const handleFileChange = async (
  event,
  setSelectedFile,
  setImagePreview,
  setFileName,
  showSnackbar
) => {
  const file = event.target.files[0];
  if (!file) return;

  if (file.size > MAX_FILE_SIZE) {
    showSnackbar("Please select a file smaller than 2MB.", "error");
    return;
  }
  if (!ALLOWED_FILE_TYPES.includes(file.type)) {
    showSnackbar("Invalid file type. Only JPEG and PNG are allowed.", "error");
    return;
  }

  try {
    const fileResult = await readFile(file);
    const base64String = fileResult.split(",")[1];

    setSelectedFile(base64String);
    setImagePreview(URL.createObjectURL(file));
    setFileName(file.name);
  } catch (error) {
    console.error("Error reading file:", error);
  }
};

export const convertImageToBase64 = async (imageUrl) => {
  try {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result.split(",")[1]);
      reader.readAsDataURL(blob);
    });
  } catch {
    console.error("Failed to convert image to Base64.");
    return null;
  }
};

export const convertImageToBase64MultiImage = async (imageUrl) => {
  try {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result.split(",")[1]);
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error("Failed to convert image to Base64.", error);
    return null;
  }
};

export const convertProofToBase64MultiImage = async (proofUrl) => {
  try {
    const response = await fetch(proofUrl);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result.split(",")[1]);
      reader.readAsDataURL(blob);
    });
  } catch {
    console.error("Failed to convert proof to Base64.");
    return null;
  }
};

export const handleProofChange = (
  event,
  setProofSelected,
  setProofPreview,
  setProofFileName,
  showSnackbar,
  setValue
) => {
  const proof = event.target.files[0];

  if (!proof) return;

  if (proof.size > MAX_FILE_SIZE) {
    showSnackbar("Please select a file smaller than 2MB.", "error");
    return;
  }

  if (!PROOF_ALLOWED_FILE_TYPES.includes(proof.type)) {
    showSnackbar(
      "Invalid file type. Only JPEG, PNG, and PDF are allowed.",
      "error"
    );
    return;
  }

  const fileUrl = URL.createObjectURL(proof);
  setProofFileName(proof.name);
  setValue("proofName", proof.name);

  if (proof.type === "application/pdf") {
    // PDF Handling
    setProofPreview("default-pdf-icon.png");
    setProofSelected(fileUrl);
  } else {
    const reader = new FileReader();
    reader.readAsDataURL(proof);
    reader.onloadend = () => {
      setProofSelected(reader.result.split(",")[1]);
      setProofPreview(reader.result);
    };
  }
};

export const readFileMultiImage = (file) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(file);
  });
};

export const handleFileChangeMultiImage = (
  event,
  setSelectedFile,
  setImagePreview,
  setFileName,
  showSnackbar
) => {
  const files = Array.from(event.target.files);

  if (files.length === 0) return;

  const validFiles = files.filter((file) => {
    if (file.size > MAX_FILE_SIZE) {
      showSnackbar(`"${file.name}" is larger than 2MB.`, "error");
      return false;
    }
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      showSnackbar(
        `"${file.name}" has an invalid format. Only JPEG, PNG, and PDF are allowed.`,
        "error"
      );
      return false;
    }
    return true;
  });

  Promise.all(validFiles.map(readFileMultiImage)).then((filePreviews) => {
    const base64Strings = filePreviews.map((result) => result.split(",")[1]);

    setSelectedFile((prev) => [...prev, ...base64Strings]);
    setImagePreview((prev) => [
      ...prev,
      ...validFiles.map((file) =>
        file.type === "application/pdf"
          ? "path/to/default-pdf-icon.png"
          : URL.createObjectURL(file)
      ),
    ]);
    setFileName((prev) => [...prev, ...validFiles.map((file) => file.name)]);
  });
};

export const urlToBase64 = async (url) => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const blob = await response.blob();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    throw new Error(`Failed to convert ${url}: ${error.message}`);
  }
};

// const convertFileToBase64 = (file) => {
//   return new Promise((resolve, reject) => {
//     const reader = new FileReader();
//     reader.onload = () => resolve(reader.result);
//     reader.onerror = (error) => reject(error);
//     reader.readAsDataURL(file);
//   });
// };

export const handleProofChangeMultiImage = (
  event,
  setProofSelected,
  setProofPreview,
  setProofFileName,
  showSnackbar,
  setValue
) => {
  const files = Array.from(event.target.files);

  if (files.length === 0) return;

  const validProofs = files.filter((proof) => {
    if (proof.size > MAX_FILE_SIZE) {
      showSnackbar(`"${proof.name}" is larger than 2MB.`, "error");
      return false;
    }
    if (!PROOF_ALLOWED_FILE_TYPES.includes(proof.type)) {
      showSnackbar(
        `"${proof.name}" has an invalid format. Only JPEG, PNG, and PDF are allowed.`,
        "error"
      );
      return false;
    }
    return true;
  });

  Promise.all(validProofs.map(readFileMultiImage)).then((proofPreviews) => {
    const base64Proofs = proofPreviews.map((result) => result.split(",")[1]);

    setProofSelected((prev) => [
      ...(Array.isArray(prev) ? prev : []),
      ...base64Proofs,
    ]);
    setProofFileName((prev) => [
      ...(Array.isArray(prev) ? prev : []),
      ...validProofs.map((file) => file.name),
    ]);
    setValue("proofName", validProofs.map((file) => file.name).join(", "));

    const fileUrls = validProofs.map((proof) => {
      const fileUrl = URL.createObjectURL(proof);
      return {
        url: fileUrl,
        isPdf: proof.type === "application/pdf",
      };
    });

    setProofPreview((prev) => [
      ...(Array.isArray(prev) ? prev : []),
      ...fileUrls,
    ]);
  });
};

export const handleAddFileChange = (
  event,
  setSelectedFile,
  setImagePreview,
  showSnackbar
) => {
  const files = Array.from(event.target.files);

  if (files.length === 0) return;

  const validFiles = files.filter((file) => {
    if (file.size > MAX_FILE_SIZE) {
      showSnackbar(`"${file.name}" must be smaller than 2MB.`, "error");
      return false;
    }
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      showSnackbar(
        `"${file.name}" has an invalid format. Only JPEG and PNG are allowed.`,
        "error"
      );
      return false;
    }
    return true;
  });

  Promise.all(validFiles.map(readFileMultiImage)).then((filePreviews) => {
    const base64Strings = filePreviews.map((result) => result.split(",")[1]);

    setSelectedFile((prev) => [
      ...(Array.isArray(prev) ? prev : []),
      ...base64Strings,
    ]);
    setImagePreview((prev) => [
      ...(Array.isArray(prev) ? prev : []),
      ...validFiles.map((file) => URL.createObjectURL(file)),
    ]);
  });
};

export const handleAddProofChange = (
  event,
  setProofSelectedFile,
  setProofPreview,
  showSnackbar
) => {
  const files = Array.from(event.target.files);

  if (files.length === 0) return;

  const validFiles = files.filter((file) => {
    if (file.size > MAX_FILE_SIZE) {
      showSnackbar(`"${file.name}" must be smaller than 2MB.`, "error");
      return false;
    }
    if (!PROOF_ALLOWED_FILE_TYPES.includes(file.type)) {
      showSnackbar(
        `"${file.name}" has an invalid format. Only JPEG, PNG, and PDF are allowed.`,
        "error"
      );
      return false;
    }
    return true;
  });

  Promise.all(validFiles.map(readFileMultiImage)).then((filePreviews) => {
    const base64Strings = filePreviews.map((result) => result.split(",")[1]);

    setProofSelectedFile((prev) => [
      ...(Array.isArray(prev) ? prev : []),
      ...base64Strings,
    ]);

    setProofPreview((prev) => [
      ...(Array.isArray(prev) ? prev : []),
      ...validFiles.map((file) =>
        file.type === "application/pdf" ? pdfImage : URL.createObjectURL(file)
      ),
    ]);

    validFiles.forEach((file) => {
      if (file.type === "application/pdf") {
        window.pdfFileUrls = window.pdfFileUrls || {};
        window.pdfFileUrls[file.name] = URL.createObjectURL(file);
      }
    });
  });
};

export const removeImageAdd = (index, setSelectedFile, setImagePreview) => {
  setSelectedFile((prev) => prev.filter((_, i) => i !== index));
  setImagePreview((prev) => prev.filter((_, i) => i !== index));
};

export const removeProofImageAdd = (
  index,
  setProofSelectedFile,
  setProofPreview
) => {
  setProofSelectedFile((prev) => prev.filter((_, i) => i !== index));
  setProofPreview((prev) => prev.filter((_, i) => i !== index));
};

export const removeImage = (
  index,
  setSelectedFile,
  setImagePreview,
  setExistingImageUrls
) => {
  setSelectedFile((prev) =>
    Array.isArray(prev) ? prev.filter((_, i) => i !== index) : []
  );
  setImagePreview((prev) =>
    Array.isArray(prev) ? prev.filter((_, i) => i !== index) : []
  );
  setExistingImageUrls((prev) =>
    Array.isArray(prev) ? prev.filter((_, i) => i !== index) : []
  );
};

export const removeProofImage = (
  index,
  setProofSelectedFile,
  setProofPreview,
  setExistingProofUrls
) => {
  setProofSelectedFile((prev) =>
    Array.isArray(prev) ? prev.filter((_, i) => i !== index) : []
  );
  setProofPreview((prev) =>
    Array.isArray(prev) ? prev.filter((_, i) => i !== index) : []
  );
  setExistingProofUrls((prev) =>
    Array.isArray(prev) ? prev.filter((_, i) => i !== index) : []
  );
};

export const removeBarImage = (
  index,
  setSelectedImage,
  setImagePreview,
  setExistingImageUrls
) => {
  setSelectedImage((prev) =>
    Array.isArray(prev) ? prev.filter((_, i) => i !== index) : []
  );
  setImagePreview((prev) =>
    Array.isArray(prev) ? prev.filter((_, i) => i !== index) : []
  );
  setExistingImageUrls((prev) =>
    Array.isArray(prev) ? prev.filter((_, i) => i !== index) : []
  );
};

export const removeBarProofImage = (
  index,
  setProofSelected,
  setProofPreview,
  setExistingProofUrls
) => {
  setProofSelected((prev) =>
    Array.isArray(prev) ? prev.filter((_, i) => i !== index) : []
  );
  setProofPreview((prev) =>
    Array.isArray(prev) ? prev.filter((_, i) => i !== index) : []
  );
  setExistingProofUrls((prev) =>
    Array.isArray(prev) ? prev.filter((_, i) => i !== index) : []
  );
};
